import React from "react";
import { C, radii, type } from "../../styles/tokens";

/**
 * Approved Brand Button (docs/BRAND_MANUAL.md, section 8)
 * Variants:
 *  - "primary": #08833E fill, white bodyStrong label (one per screen)
 *  - "secondary": white, 1.5 navy border, navy label
 *  - "dark": navy fill, white label (banner action)
 *  - "text": no fill, primary label
 *  - "subtle": textMuted, label size
 *  - "destructive": danger fill, white label
 *
 * Sizes:
 *  - "default": 48px height, 16px 24px padding
 *  - "small": 40px height, 10px 18px padding
 */
export function BrandButton({
  variant = "primary",
  size = "default",
  fullWidth = false,
  disabled = false,
  loading = false,
  children,
  onClick,
  type: buttonType = "button",
  style = {},
  className = "",
  title,
  icon: Icon,
  ...rest
}) {
  const [hovered, setHovered] = React.useState(false);

  const isSmall = size === "small";
  const height = isSmall ? 40 : 48;
  const padding = isSmall ? "0 18px" : "0 24px";
  const typography = isSmall ? type.label : type.bodyStrong;

  let bg = C.primary;
  let color = "#ffffff";
  let border = "none";
  let cursor = disabled ? "not-allowed" : "pointer";
  let opacity = disabled ? 0.55 : 1;

  if (variant === "primary") {
    bg = hovered && !disabled ? C.primaryPressed : C.primary;
    color = "#ffffff";
    border = "none";
  } else if (variant === "secondary") {
    bg = hovered && !disabled ? C.surfaceSunken : "#ffffff";
    color = C.navy;
    border = `1.5px solid ${C.navy}`;
  } else if (variant === "dark") {
    bg = hovered && !disabled ? C.navyDark : C.navy;
    color = "#ffffff";
    border = "none";
  } else if (variant === "text") {
    bg = "transparent";
    color = hovered && !disabled ? C.primaryPressed : C.primary;
    border = "none";
  } else if (variant === "subtle") {
    bg = "transparent";
    color = hovered && !disabled ? C.navy : C.textMuted;
    border = "none";
  } else if (variant === "destructive") {
    bg = hovered && !disabled ? C.dangerPressed : C.danger;
    color = "#ffffff";
    border = "none";
  }

  return (
    <button
      type={buttonType}
      disabled={disabled || loading}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={title}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        height,
        minHeight: height,
        padding,
        borderRadius: radii.pill,
        backgroundColor: bg,
        color,
        border,
        fontSize: typography.fontSize,
        lineHeight: typography.lineHeight,
        fontWeight: typography.fontWeight,
        letterSpacing: typography.letterSpacing,
        fontFamily: "inherit",
        cursor,
        opacity,
        width: fullWidth ? "100%" : "auto",
        transition: "background-color 0.15s ease-out, border-color 0.15s ease-out, opacity 0.15s ease-out",
        boxSizing: "border-box",
        textDecoration: "none",
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {loading ? (
        <span
          style={{
            display: "inline-block",
            width: 18,
            height: 18,
            border: `2px solid ${color}`,
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "tp-spin 0.6s linear infinite",
          }}
        />
      ) : (
        <>
          {Icon && <Icon size={isSmall ? 16 : 18} strokeWidth={2} />}
          {children}
        </>
      )}
    </button>
  );
}
